import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo } from "@/components/site/Seo";

const FAQS = [
  {
    q: "What is Support Coordination?",
    a: "Support Coordination is a funded NDIS service that helps you understand your plan, connect with providers, and build the skills to manage your supports. Your Support Coordinator works for you, not the providers, to make sure your plan is working as intended.",
  },
  {
    q: "Who can use Kernected Disability?",
    a: "Any NDIS participant with Support Coordination funded in their plan can choose us as their provider. If you're not sure what's funded in your plan, send us a short enquiry and we'll help you check.",
  },
  {
    q: "How much does it cost?",
    a: "Our services are billed against the Capacity Building – Support Coordination budget in your NDIS plan, at the prices set by the NDIS Price Guide. That means there is no extra cost to you when funding is available in your plan.",
  },
  {
    q: "How do I switch from my current coordinator?",
    a: "It's straightforward. We help draft a short notice to your current provider, set up a new service agreement with us, and ensure no gaps in your supports during the transition.",
  },
  {
    q: "Do you support participants with complex needs?",
    a: "Yes. We work with participants navigating complex situations and coordinate alongside health, housing and other systems to keep your supports stable and on track.",
  },
  {
    q: "How quickly will you respond to my enquiry?",
    a: "We respond to all enquiries within one business day. If your situation is urgent, please mention this in your message and we'll prioritise getting back to you.",
  },
  {
    q: "Which areas of Australia do you cover?",
    a: "We support participants across Australia, with a mix of in person and remote coordination. Tell us where you live in your enquiry and we'll confirm what's possible.",
  },
  {
    q: "Will you help me prepare for my plan review?",
    a: "Absolutely. A big part of our role is preparing a clear, evidence based report for your plan reassessment so the NDIA understands what's working and what you need next.",
  },
];

export default function FaqPage() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <div data-testid="faq-page">
      <Seo
        title="NDIS Support Coordination FAQ"
        description="Clear answers to common questions about NDIS Support Coordination, switching providers, costs, plan reviews and how Kernected Disability can help."
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <section className="container-narrow py-16 md:py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-[#1B3A6B]" aria-hidden="true" />
            Frequently asked questions
          </span>
          <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.75rem] font-medium tracking-tight text-slate-900 leading-[1.05]">
            Clear answers to common NDIS questions
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">
            Can't find what you're looking for? Send us a short message and a
            real person will reply within one business day.
          </p>
        </div>

        <div className="mt-12 bg-white border border-slate-200/70 rounded-3xl p-4 md:p-8">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((item, idx) => (
              <AccordionItem
                key={item.q}
                value={`item-${idx}`}
                className="border-b border-slate-200 last:border-b-0"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger className="py-5 text-left font-display text-lg md:text-xl font-semibold text-slate-900 hover:text-[#1B3A6B] hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base md:text-lg text-slate-700 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-12 bg-[#1B3A6B] text-white rounded-3xl p-10 md:p-12 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl font-semibold">Still have questions?</h2>
            <p className="mt-3 text-slate-100/90 text-lg">
              We're happy to chat through anything. No obligation.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="faq-cta-contact"
            className="justify-self-start lg:justify-self-end inline-flex items-center gap-2 bg-white text-[#1B3A6B] hover:bg-[#F0F4F8] px-6 py-3.5 rounded-lg font-medium"
          >
            Get in touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
